import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Check, X, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';

// Reusable add/rename/reorder/delete table for a simple master list (categories, uoms…).
// api: { list, create, update, remove }. labels: { title, desc, name, namePh, add, empty, usage } (i18n keys).
export default function MasterTable({ api, prefix, labels, Icon }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.list().then(setRows).catch(() => {}); }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const nm = newName.trim();
    if (!nm) return;
    setBusy(true);
    try { await api.create({ name: nm }); setNewName(''); toast.success(t('mfg.saved')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };
  const saveName = async (r) => {
    const nm = editName.trim();
    if (!nm) { setEditId(null); return; }
    try { await api.update(r.id, { name: nm }); setEditId(null); toast.success(t('mfg.saved')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
  };
  const del = async (r) => {
    if (!window.confirm(t('mfg.confirmDelete', { name: r.name }))) return;
    try { await api.remove(r.id); toast.success(t('mfg.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const a = rows[idx], b = rows[j];
    try {
      await Promise.all([api.update(a.id, { sort_order: b.sort_order }), api.update(b.id, { sort_order: a.sort_order })]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          {Icon && <Icon size={18} weight="duotone" className="text-orange-500" />} {t(labels.title)}
        </h2>
        <p className="text-sm text-zinc-500 mt-0.5">{t(labels.desc)}</p>
      </div>

      {canWrite && (
        <div className="flex gap-2 max-w-md" data-testid={`${prefix}-add-bar`}>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder={t(labels.namePh)} className="h-10" data-testid={`${prefix}-new-input`} />
          <Button onClick={add} disabled={busy} data-testid={`${prefix}-add-btn`} className="h-10 bg-teal-700 hover:bg-teal-800 shrink-0"><Plus size={18} className="mr-1" weight="bold" /> {t(labels.add)}</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden max-w-2xl">
        <table className="w-full text-sm" data-testid={`${prefix}-table`}>
          <thead><tr className="bg-zinc-100 text-zinc-700 text-xs border-b border-zinc-200">
            <th className="text-left font-semibold px-4 py-3 w-12">#</th>
            <th className="text-left font-semibold px-4 py-3">{t(labels.name)}</th>
            <th className="text-right font-semibold px-4 py-3">{t(labels.usage)}</th>
            <th className="text-right font-semibold px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-16 text-center">
                {Icon && <Icon size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />}
                <p className="text-sm text-zinc-500" data-testid={`${prefix}-empty`}>{t(labels.empty)}</p>
              </td></tr>
            ) : rows.map((r, idx) => (
              <tr key={r.id} className="border-t border-zinc-200 hover:bg-teal-50/50" data-testid={`${prefix}-row-${r.id}`}>
                <td className="px-4 py-2.5 text-zinc-400 tabular-nums">{idx + 1}</td>
                <td className="px-4 py-2.5">
                  {editId === r.id ? (
                    <div className="flex gap-1 items-center">
                      <Input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveName(r); if (e.key === 'Escape') setEditId(null); }} className="h-8 max-w-[220px]" data-testid={`${prefix}-edit-input-${r.id}`} />
                      <Button size="sm" onClick={() => saveName(r)} data-testid={`${prefix}-edit-save-${r.id}`} className="h-8 px-2 bg-teal-700 hover:bg-teal-800"><Check size={15} /></Button>
                      <Button size="sm" variant="outline" onClick={() => setEditId(null)} className="h-8 px-2"><X size={15} /></Button>
                    </div>
                  ) : (
                    <span className="font-medium text-zinc-800">{r.name}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right"><Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200 tabular-nums">{r.item_count ?? 0}</Badge></td>
                <td className="px-4 py-2.5">
                  {canWrite && editId !== r.id && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`${prefix}-up-${r.id}`} className="h-8 px-2 text-zinc-500" title={t('mfg.category.moveUp')}><ArrowUp size={15} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} data-testid={`${prefix}-down-${r.id}`} className="h-8 px-2 text-zinc-500" title={t('mfg.category.moveDown')}><ArrowDown size={15} /></Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditId(r.id); setEditName(r.name); }} data-testid={`${prefix}-edit-${r.id}`} className="h-8 px-2 text-zinc-700 border-zinc-200 hover:bg-zinc-100" title="編集"><PencilSimple size={15} /></Button>
                      {canDeleteMfg && <Button size="sm" variant="outline" onClick={() => del(r)} data-testid={`${prefix}-delete-${r.id}`} className="h-8 px-2 text-red-600 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300" title="削除"><Trash size={15} /></Button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
